import { defineStore } from "pinia";
import { useAboutYouStore } from "./aboutYou";

export const Independent = "independent";
export const NeedsHelp = "needs-help";
export const Dependant = "dependant";
export const NonPermanentWheelchair = "non-permanent-wheelchair";
export const PermanentWheelchair = "permanent-wheelchair";
export const DailyNursing = "daily-nursing";
export const Bedridden = "bedridden";
export const MinorHelp = "minor-help";
export const MajorHelp = "major-help";
export const Unable = "unable";
export const Continent = "continent";
export const OccasionalAccident = "occasional-accident";
export const Incontinent = "incontinent";
export const Stable = "stable";
export const Deteriorating = "deteriorating";
export const RapidDeterioration = "rapid-deterioration";

export type DressBathYourself = typeof Independent
    | typeof NeedsHelp
    | typeof Dependant
    | null;

export type ApplicantFeeding = typeof Independent
    | typeof Deteriorating
    | typeof RapidDeterioration
    | null;

export type ApplicantProgression = typeof Stable
    | typeof NeedsHelp
    | typeof Unable
    | null;

export type ApplicantMobility = typeof Independent
    | typeof NeedsHelp
    | typeof NonPermanentWheelchair
    | typeof PermanentWheelchair
    | typeof DailyNursing
    | typeof Bedridden
    | null;

export type ApplicantTransferring = typeof Independent
    | typeof MinorHelp
    | typeof MajorHelp
    | typeof Unable
    | null;

export type ApplicantToilet = typeof Independent
    | typeof Continent
    | typeof OccasionalAccident
    | typeof Incontinent
    | null;

export type DailyActivitiesStore = {
    dressYourself: DressBathYourself,
    applicantMobility: ApplicantMobility,
    applicantTransferring: ApplicantTransferring,
    applicantBladder: ApplicantToilet,
    applicantBowels: ApplicantToilet,
    applicantBathing: DressBathYourself,
    applicantFeeding: ApplicantFeeding,
    applicantProgression: ApplicantProgression,
}

export type ActivityLabelGroup =
    "dressYourself" |
    "applicantMobility" |
    "applicantTransferring" |
    "applicantBladder" |
    "applicantBowels" |
    "applicantBathing" |
    "applicantFeeding" |
    "applicantProgression";

type DressYourselfActivities = "independent" | "needs-help" | "dependant";
type ApplicantMobilityActivities =
    "independent"
    | "needs-help"
    | "non-permanent-wheelchair"
    | "permanent-wheelchair"
    | "daily-nursing"
    | "bedridden";
type ApplicantTransferringActivities = "independent" | "minor-help" | "major-help" | "unable";
type ApplicantBladderActivities = "continent" | "occasional-accident" | "incontinent";
type ApplicantBowelsActivities = "continent" | "occasional-accident" | "incontinent";
type ApplicantBathingActivities = "independent" | "needs-help" | "dependant";
type ApplicantFeedingActivities = "independent" | "needs-help" | "unable";
type ApplicantProgressionActivities = "stable" | "deteriorating" | "rapid-deterioration";

type DailyActivitiesLabelsType = {
    dressYourself: Record<DressYourselfActivities, string>,
    applicantMobility: Record<ApplicantMobilityActivities, string>,
    applicantTransferring: Record<ApplicantTransferringActivities, string>,
    applicantBladder: Record<ApplicantBladderActivities, string>,
    applicantBowels: Record<ApplicantBowelsActivities, string>,
    applicantBathing: Record<ApplicantBathingActivities, string>,
    applicantFeeding: Record<ApplicantFeedingActivities, string>,
    applicantProgression: Record<ApplicantProgressionActivities, string>
};

export const DailyActivitiesLabels: DailyActivitiesLabelsType = {
    dressYourself: {
        [Independent]: "Independent (Including Buttons, Zips, Laces, etc.)",
        [NeedsHelp]: "Needs Help, But Can Do About Half Unaided",
        [Dependant]: "Dependant (requires full assistance)",
    },
    applicantMobility: {
        [Independent]: "Independent (Needs No Assistance)",
        [NeedsHelp]: "Walks With Assistance (Frame/Stick etc.)",
        [NonPermanentWheelchair]: "Wheelchair Use - Non-Permanent",
        [PermanentWheelchair]: "Wheelchair Use - Permanent",
        [DailyNursing]: "In Need Of Daily Nursing Care",
        [Bedridden]: "Bedridden",
    },
    applicantTransferring: {
        [Independent]: "Independent",
        [MinorHelp]: "Minor Help, Can Sit Unaided",
        [MajorHelp]: "Major Help",
        [Unable]: "Unable, No Sitting Balance",
    },
    applicantBladder: {
        [Continent]: "Continent",
        [OccasionalAccident]: "Occasional Accident (Once A Week)",
        [Incontinent]: "Incontinent/Catheterised/Unable To Manage Alone",
    },
    applicantBowels: {
        [Continent]: "Continent",
        [OccasionalAccident]: "Occasional Accident (Once A Week)",
        [Incontinent]: "Incontinent (Or Requires Enema)",
    },
    applicantBathing: {
        [Independent]: "Independent",
        [NeedsHelp]: "Needs Some Assistance",
        [Dependant]: "Dependant",
    },
    applicantFeeding: {
        [Independent]: "Independent",
        [NeedsHelp]: "Needs Some Help Cutting, Spreading Butter etc.",
        [Unable]: "Unable (Naso-Gastric Tube/PEG Tube In Place)",
    },
    applicantProgression: {
        [Stable]: "Stable (No/minimal change)",
        [Deteriorating]: "Deteriorating (Impact To 2 Or More ADLs/Acute Episodes)",
        [RapidDeterioration]: "Rapid Deterioration",
    },
};

export const useDailyActivitiesStore = defineStore("dailyActivities", {
    state: () : DailyActivitiesStore => {
        return {
            dressYourself: null,
            applicantMobility: null,
            applicantTransferring: null,
            applicantBladder: null,
            applicantBowels: null,
            applicantBathing: null,
            applicantFeeding: null,
            applicantProgression: null,
        };
    },
    getters: {
        getDailyActivities(state) {
            return {
                dressYourself: state.dressYourself,
                applicantMobility: state.applicantMobility,
                applicantTransferring: state.applicantTransferring,
                applicantBladder: state.applicantBladder,
                applicantBowels: state.applicantBowels,
                applicantBathing: state.applicantBathing,
                applicantFeeding: state.applicantFeeding,
                applicantProgression: state.applicantProgression,
            };
        },
        dailyActivitiesRequired(): boolean {
            const store = useAboutYouStore();
            return !!store.medical;
        },
    },
});
